import React, { useEffect, useState } from "react";
import axios from "axios";
import { FileText, X, Calendar, Tag, Users, Building2, ChevronRight } from "lucide-react";
import { useLanguage } from "../../i18n/LanguageContext";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const STATUS_CONFIG = {
  "Attivo": { color: "#00FF66", bg: "#00FF66" },
  "In registrazione": { color: "#FF9F0A", bg: "#FF9F0A" },
  "Scaduto": { color: "#FF3B30", bg: "#FF3B30" },
};

function PatentModal({ patent, onClose }) {
  const cfg = STATUS_CONFIG[patent.status] || STATUS_CONFIG["Attivo"];
  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <div className="bg-[#0D0F12] border border-white/[0.04] w-full max-w-2xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/[0.04]">
          <div className="flex items-center gap-3">
            <FileText className="w-5 h-5 text-[#00E5FF]" />
            <h2 className="text-sm font-bold text-white" style={{ fontFamily: "Chivo, sans-serif" }}>Patent Report</h2>
          </div>
          <button onClick={onClose} className="text-[#A0AEC0] hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <div className="overflow-y-auto flex-1 p-6 space-y-4">
          <div>
            <span className="text-[10px] px-2 py-0.5 border tracking-widest uppercase" style={{ color: cfg.color, borderColor: `${cfg.color}40`, backgroundColor: `${cfg.color}10` }}>{patent.status}</span>
            <h3 className="text-base font-bold text-white mt-3" style={{ fontFamily: "Chivo, sans-serif" }}>{patent.title}</h3>
            <p className="text-xs text-[#00E5FF] mt-1">{patent.company}</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {[
              ["N. Brevetto", patent.patent_number],
              ["Data Deposito", patent.filing_date],
              ["Classificazione IPC", patent.ipc_class],
              ["Classificazione CPC", patent.cpc_class],
            ].map(([l, v]) => (
              <div key={l} className="bg-[#050505] border border-white/[0.04] p-3">
                <p className="text-[10px] text-[#A0AEC0] tracking-widest uppercase mb-1">{l}</p>
                <p className="text-xs text-white font-medium" style={{ fontFamily: "JetBrains Mono, monospace" }}>{v || "—"}</p>
              </div>
            ))}
          </div>
          <div className="bg-[#050505] border border-white/[0.04] p-4">
            <p className="text-[10px] text-[#A0AEC0] tracking-widest uppercase mb-2">Abstract</p>
            <p className="text-xs text-[#CBD5E1] leading-relaxed" style={{ fontFamily: "JetBrains Mono, monospace" }}>{patent.abstract}</p>
          </div>
          {patent.inventors?.length > 0 && (
            <div className="bg-[#050505] border border-white/[0.04] p-4">
              <p className="text-[10px] text-[#A0AEC0] tracking-widest uppercase mb-3">Inventori</p>
              <div className="flex flex-wrap gap-2">
                {patent.inventors.map((inv, i) => (
                  <div key={`${inv}-${i}`} className="flex items-center gap-1.5 px-2 py-1 bg-[#1A1D24] border border-white/[0.04]">
                    <Users className="w-3 h-3 text-[#A0AEC0]" />
                    <span className="text-xs text-[#CBD5E1]">{inv}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="bg-[#050505] border border-white/[0.04] p-4">
            <p className="text-[10px] text-[#A0AEC0] tracking-widest uppercase mb-2">Titolare</p>
            <div className="flex items-center gap-2">
              <Building2 className="w-4 h-4 text-[#A0AEC0]" />
              <span className="text-sm text-white">{patent.company}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function PatentsPage() {
  const { t } = useLanguage();
  const [patents, setPatents] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [selected, setSelected] = useState(null);
  const [filterCompany, setFilterCompany] = useState("all");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/tech/patents`, { withCredentials: true })
      .then(r => {
        setPatents(r.data);
        const unique = [...new Set(r.data.map(p => p.company))];
        setCompanies(unique);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const filtered = filterCompany === "all" ? patents : patents.filter(p => p.company === filterCompany);
  const grouped = filtered.reduce((acc, p) => {
    if (!acc[p.company]) acc[p.company] = [];
    acc[p.company].push(p);
    return acc;
  }, {});

  const totalActive = patents.filter(p => p.status === "Attivo").length;
  const totalPending = patents.filter(p => p.status === "In registrazione").length;

  return (
    <div className="p-6 sm:p-8 space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white tracking-tight" style={{ fontFamily: "Chivo, sans-serif" }}>{t("tech.patents.title")}</h1>
          <p className="text-xs text-[#A0AEC0] mt-1 tracking-widest uppercase">{t("tech.patents.subtitle")}</p>
        </div>
        <select
          value={filterCompany}
          onChange={e => setFilterCompany(e.target.value)}
          className="bg-[#050505] border border-white/[0.04] text-white px-3 py-2 text-xs focus:outline-none focus:border-[#00E5FF]"
        >
          <option value="all">Tutte le aziende</option>
          {companies.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {[
          { label: "Brevetti Totali", value: patents.length, color: "#00E5FF" },
          { label: "Attivi", value: totalActive, color: "#00FF66" },
          { label: "In Registrazione", value: totalPending, color: "#FF9F0A" },
        ].map((s) => (
          <div key={s.label} className="bg-[#0D0F12] border border-white/[0.04] p-4">
            <p className="text-[10px] text-[#A0AEC0] tracking-widest uppercase mb-2">{s.label}</p>
            <p className="text-2xl font-bold" style={{ color: s.color, fontFamily: "JetBrains Mono, monospace" }}>{s.value}</p>
          </div>
        ))}
      </div>

      {loading ? (
        <div className="text-center py-12 text-[#A0AEC0]">Caricamento brevetti...</div>
      ) : Object.entries(grouped).map(([company, pats]) => (
        <div key={company} className="bg-[#0D0F12] border border-white/[0.04]">
          <div className="flex items-center gap-3 px-5 py-3 border-b border-white/[0.04]">
            <Building2 className="w-4 h-4 text-[#00E5FF]" />
            <span className="text-sm text-white font-medium">{company}</span>
            <span className="ml-auto text-xs text-[#A0AEC0]">{pats.length} brevetti</span>
          </div>
          <div className="divide-y divide-[#1F242D]">
            {pats.map(p => {
              const cfg = STATUS_CONFIG[p.status] || STATUS_CONFIG["Attivo"];
              return (
                <div key={p.id} data-testid="patent-row" className="flex items-center gap-4 px-5 py-4 hover:bg-[#1A1D24] transition-colors group">
                  <FileText className="w-4 h-4 text-[#A0AEC0] flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-white font-medium">{p.title}</p>
                    <div className="flex items-center gap-3 mt-1 flex-wrap">
                      <span className="text-[10px] text-[#A0AEC0]" style={{ fontFamily: "JetBrains Mono, monospace" }}>{p.patent_number}</span>
                      <div className="flex items-center gap-1 text-[10px] text-[#A0AEC0]">
                        <Calendar className="w-3 h-3" />{p.filing_date}
                      </div>
                      <div className="flex items-center gap-1 text-[10px] text-[#A0AEC0]">
                        <Tag className="w-3 h-3" />{p.ipc_class}
                      </div>
                    </div>
                  </div>
                  <span className="text-[10px] px-2 py-0.5 border flex-shrink-0" style={{ color: cfg.color, borderColor: `${cfg.color}40`, backgroundColor: `${cfg.color}10` }}>{p.status}</span>
                  <button
                    data-testid={`patent-detail-${p.id}`}
                    onClick={() => setSelected(p)}
                    className="flex items-center gap-1 text-[#A0AEC0] hover:text-[#00E5FF] transition-colors opacity-0 group-hover:opacity-100"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      ))}

      {selected && <PatentModal patent={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}
